import { useRef, useState } from "react";
import {
  Annotation,
  ImageAnnotator,
  UserSelectAction,
  ImageAnnotationPopup,
  PopupProps,
  useAnnotator,
  DrawingStyle,
  ImageAnnotation,
} from "@annotorious/react";
import { Box, Text } from "@chakra-ui/react";
import { Button } from "@/components/ui/button";
import { LuTrash2 } from "react-icons/lu";
import AnnotationBadges from "../AnnotationBadges";
import AnnotationPopup from "../AnnotationPopup";
import { MdLayers, MdLayersClear } from "react-icons/md";
import { isAfter } from "date-fns";

interface IImageAnnotationSection {
  drawerHeight: number;
  snapPoints: number[];
  editMode: boolean;
  updateMode: boolean;
  image: File | string;
  observationDate?: string;
  annotations: Annotation[];
  selectedAnnotations: {
    annotation: ImageAnnotation;
    editable?: boolean | undefined;
  }[];
  showBadges?: boolean;
  enablePopups?: boolean;
  isCompletedStatus?: boolean;
}

function ImageAnnotationSection({
  drawerHeight,
  snapPoints,
  editMode,
  updateMode,
  selectedAnnotations,
  image,
  observationDate,
  annotations,
  showBadges = true,
  enablePopups = true,
  isCompletedStatus = false,
}: IImageAnnotationSection) {
  const imageElement = useRef<HTMLImageElement>(null);
  const [showMask, setshowMask] = useState<boolean>(false);
  // TO-DO: replace "2030-01-01" with the date when the mask generation is enabled in the ML-Model infrastructure
  const enableMaskOverlayFeature = isCompletedStatus && observationDate
    ? isAfter(new Date(observationDate), new Date("2026-08-19"))
    : false;
  const anno = useAnnotator();
  const annotatorStyles = () => {
    return (
      {
        fill: "rgba(0, 0, 0, 0)",
        stroke: "#30fd28",
        strokeWidth: 1.5
      }
    );
  };

  return (
    <Box
      position="relative"
      height={{
        base:
          drawerHeight > snapPoints[1]
            ? `${(1 - snapPoints[1]) * 100}%`
            : `${(1 - drawerHeight) * 100}%`,
        md: "100%",
      }}
      transition="height 0.3s ease"
    >
      {updateMode && (
        <Button
          variant="surface"
          position="absolute"
          bottom={4}
          left={2}
          zIndex="10"
          size={["xs", null, "sm"]}
          title="Delete selected annotation"
          aria-label="Delete selected annotation"
          disabled={editMode ? selectedAnnotations.length === 0 : true}
          onClick={() =>
            anno.removeAnnotation(selectedAnnotations[0].annotation)}
          colorPalette={
            editMode && selectedAnnotations.length > 0 ? "red" : "gray"
          }
        >
          <LuTrash2 />
          {editMode && selectedAnnotations.length > 0 && "Delete Fragment"}
        </Button>
      )}
      {enableMaskOverlayFeature && (
        <Button
          variant="subtle"
          position="absolute"
          bottom={16}
          left={2}
          zIndex="10"
          size={["xs", null, "sm"]}
          title="Show fragment mask overlay"
          aria-label="Show fragment mask overlay"
          onClick={() =>
            setshowMask(!showMask)}
        >
          {showMask? <MdLayersClear /> : <MdLayers />}
          <Text hideBelow="md">{showMask? "Hide mask" : "Show mask"}</Text>
        </Button>
      )}
      <Box
        height="100%"
        maxHeight="100%"
        bg="gray.200"
        display="grid"
        gridTemplateRows="minmax(0px, 1fr)"
        gridTemplateColumns="minmax(0px, 1fr)"
        placeItems="center"
        placeContent="center"
        css={{
          base: {
            "& > div": {
              maxHeight: "100%",
              width: "100%",
              display: "grid !important",
              placeItems: "center",
              gridTemplateRows: "minmax(0px, 1fr)",
            },
          },
          sm: null,
        }}
      >
        <ImageAnnotator
          drawingEnabled={editMode && updateMode}
          userSelectAction={
            editMode ? UserSelectAction.EDIT : UserSelectAction.SELECT
          }
          style={annotatorStyles as DrawingStyle}
        >
          <img
            ref={imageElement}
            src={
              typeof image === "string"
                ? showMask
                  ? image.replace("observation.webp", "segmentation-masks/observation_overlay.png")
                  : image
                : URL.createObjectURL(image)
            }
            alt="Rack observation"
            height="100%"
            style={{ display: "inline-block", maxHeight: "100%" }}
          />
        </ImageAnnotator>
        {enablePopups && (
          <Box zIndex="1500" position="relative">
            <ImageAnnotationPopup
              popup={(props: PopupProps) => (
                <AnnotationPopup annotation={props.annotation} />
              )}
            />
          </Box>
        )}
        {showBadges && (
          <Box zIndex="1000" position="relative">
            {annotations.map((a) => (
              <AnnotationBadges
                key={a.id}
                imageElement={imageElement.current}
                annotation={a as ImageAnnotation}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ImageAnnotationSection;
